"use client";

import { Loader2 } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { RAIL_POPOVER_SEARCH_INPUT_CLASS } from "@/features/admin-shell/_logic/constants";
import { FetchSwitcherRow } from "@/fetch-ui/FetchSwitcherRow";
import { FetchSwitcherSearchField } from "@/fetch-ui/FetchSwitcherSearchField";

import { SWITCHER_LIST_HEIGHT_CLASS } from "../_logic/constants";
import type { SwitcherSearchState } from "../_types/ui-types";

/** One row's display, derived from a list item. */
export interface SwitcherOption {
  key: number | string;
  /** Leading glyph: an org avatar or a user avatar. Rendered decoratively, so it must not be the row's name. */
  icon: React.ReactNode;
  label: string;
  /** Optional second line under the label, e.g. an email. */
  sublabel?: string;
  /** Extra content before the check glyph (e.g. a status badge). */
  trailing?: React.ReactNode;
  /** Whether this is the current value, checkmarked like the org switcher's current org. */
  isCurrent?: boolean;
}

/** The per-flow copy a panel needs. Grouped because these three always travel together. */
export interface SwitcherSearchLabels {
  /** Search field placeholder and accessible name, e.g. "Find organization...". */
  placeholder: string;
  /** Accessible name for the results listbox, e.g. "Organizations". */
  listLabel: string;
  /** Plural noun for the empty state, e.g. "organizations". */
  noun: string;
}

interface SwitcherSearchPanelProps<T> {
  search: SwitcherSearchState<T>;
  toOption: (item: T) => SwitcherOption;
  onSelect: (item: T) => void;
  labels: SwitcherSearchLabels;
  /** Inline explanation shown above the list (e.g. why a picked row can't be used). */
  notice?: string | null;
  /** True while a pick is being processed. Freezes selection so a second click can't race it. */
  isBusy?: boolean;
  /**
   * A row pinned above the (possibly paged, possibly filtered) results, typically the current
   * value, so it's always visible without depending on where it happens to fall in "most recent" /
   * search results. Deduped by `key` from the results below, in case it's also on the loaded page.
   * Non-interactive (its click is a no-op): a caller pinning the current value has no new selection
   * to make from it. Rendered via the same `FetchSwitcherRow` as a normal option (not the muted
   * `disabled` styling), since it's still the current, live value, just not a pick target.
   */
  pinnedOption?: SwitcherOption;
}

/**
 * The body of a switcher-style popover: `FetchSwitcherSearchField` over a `FetchSwitcherRow` listbox,
 * with the org switcher's arrow-key/Enter navigation and combobox ARIA wiring (real focus stays on the
 * search input; the active row is conveyed by `aria-activedescendant`). Unlike `OrgQuickSwitchPopover`
 * the list is server-paged, so it isn't virtualized. A sentinel row at the end pulls the next page
 * on scroll when no search is active.
 *
 * @param props - See {@link SwitcherSearchPanelProps}.
 * @returns The search field and results list.
 */
export function SwitcherSearchPanel<T>({
  search,
  toOption,
  onSelect,
  labels,
  notice,
  isBusy = false,
  pinnedOption,
}: SwitcherSearchPanelProps<T>): React.ReactElement {
  // Hooks
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  // States
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  // Derived values
  const { searchTerm, displayItems, isLoading, isSearching, isLoadingMore, hasMore, sentinelRef } = search;
  const hasTerm = searchTerm.trim().length > 0;
  const isShowingSpinner = isLoading || (hasTerm && isSearching);
  // Recomputed only when the page or the mapping itself changes, not on every keystroke/arrow-key
  // render, which would otherwise re-run `toOption` (and rebuild every row's avatar element) for the
  // whole page each time. Callers that want this to hold across renders should memoize `toOption`.
  // The pinned row (if any) is filtered out here rather than left to the caller, so it never appears
  // twice just because it also happens to fall on the currently-loaded page.
  const options = useMemo(() => {
    const mapped = displayItems.map((item) => ({ item, option: toOption(item) }));
    return pinnedOption ? mapped.filter(({ option }) => option.key !== pinnedOption.key) : mapped;
  }, [displayItems, toOption, pinnedOption]);
  // Clamped rather than reset, so the highlight survives a page of results arriving mid-navigation.
  // Indexes into `options` (post-dedup), not `displayItems`, because the pinned row is never part
  // of keyboard navigation, so this must track the same list `options.map` below actually renders.
  const highlighted = Math.min(highlightedIndex, Math.max(options.length - 1, 0));
  const optionId = (index: number): string => `${listboxId}-option-${index}`;
  const activeOptionId = options.length > 0 && !isShowingSpinner ? optionId(highlighted) : undefined;
  const emptyMessage = hasTerm ? `No ${labels.noun} found` : `No ${labels.noun} available`;

  // Handlers
  const handleSearchChange = (value: string): void => {
    search.setSearchTerm(value);
    setHighlightedIndex(0);
  };

  const handleSelect = (item: T): void => {
    if (isBusy) return;
    onSelect(item);
  };

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    // Clear the term before letting Escape bubble to Radix and close the whole popover.
    if (event.key === "Escape" && hasTerm) {
      event.preventDefault();
      event.stopPropagation();
      handleSearchChange("");
      return;
    }

    if (options.length === 0 || isShowingSpinner) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex(Math.min(highlighted + 1, options.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex(Math.max(highlighted - 1, 0));
    } else if (event.key === "Home") {
      event.preventDefault();
      setHighlightedIndex(0);
    } else if (event.key === "End") {
      event.preventDefault();
      setHighlightedIndex(options.length - 1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const active = options[highlighted];
      if (active) handleSelect(active.item);
    }
  };

  // Effects
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    document.getElementById(`${listboxId}-option-${highlighted}`)?.scrollIntoView({ block: "nearest" });
  }, [highlighted, listboxId]);

  // Render
  return (
    <div onKeyDown={handleKeyDown}>
      <FetchSwitcherSearchField
        value={searchTerm}
        onChange={handleSearchChange}
        placeholder={labels.placeholder}
        inputClassName={RAIL_POPOVER_SEARCH_INPUT_CLASS}
        inputRef={inputRef}
        comboboxProps={{ "aria-controls": listboxId, "aria-activedescendant": activeOptionId }}
      />

      {notice && (
        <p role="alert" className="border-b border-amber-100 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
          {notice}
        </p>
      )}

      {/* The list changes asynchronously (debounced search, paged scroll), which is otherwise silent. */}
      <div aria-live="polite" className="sr-only">
        {isShowingSpinner ? "" : options.length === 0 ? emptyMessage : `${options.length} ${labels.noun}`}
      </div>

      <div
        role="listbox"
        id={listboxId}
        aria-label={labels.listLabel}
        aria-busy={isShowingSpinner || isBusy}
        className={`${SWITCHER_LIST_HEIGHT_CLASS} overflow-y-auto overflow-x-hidden p-1`}
      >
        {isShowingSpinner && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="size-5 animate-spin text-gray-500" aria-hidden="true" />
          </div>
        )}

        {!isShowingSpinner && pinnedOption && (
          <div className="mb-1 border-b border-gray-100 pb-1">
            <FetchSwitcherRow
              icon={pinnedOption.icon}
              label={pinnedOption.label}
              sublabel={pinnedOption.sublabel}
              trailing={pinnedOption.trailing}
              isSelected={pinnedOption.isCurrent}
              onClick={() => {}}
            />
          </div>
        )}

        {!isShowingSpinner && options.length === 0 && (
          <p className="px-3 py-6 text-center text-sm font-medium text-gray-500">{emptyMessage}</p>
        )}

        {!isShowingSpinner &&
          options.map(({ item, option }, index) => (
            <FetchSwitcherRow
              key={option.key}
              id={optionId(index)}
              role="option"
              icon={option.icon}
              label={option.label}
              sublabel={option.sublabel}
              trailing={option.trailing}
              isSelected={option.isCurrent}
              isHighlighted={index === highlighted}
              onClick={() => handleSelect(item)}
            />
          ))}

        {!isShowingSpinner && !hasTerm && hasMore && (
          <div ref={sentinelRef} className="flex items-center justify-center py-2">
            {isLoadingMore && <Loader2 className="size-4 animate-spin text-gray-500" aria-hidden="true" />}
          </div>
        )}
      </div>
    </div>
  );
}
